import CustomInputField from "@components/FormFields/CustomInputField";
import { LocationSelectMenu } from "@components/Locations/LocationSelectMenu";
import { CircularProgressIndicator } from "@components/shared/CircularProgressIndicator";
import { Button } from "@components/ui/button";
import useDialog from "@hooks/useDialog";
import useGzLog from "@hooks/useGzLog";
import { Screen } from "@hooks/useScreens/types";
import { env } from "@src/env.mjs";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiMonitor } from "react-icons/fi";
import { MdArrowForward, MdUpdate } from "react-icons/md";
import { Location } from "types";
import DataFetchStatus from "types/DataFetchStatus";
import { ScreenIdType, screenTypes } from "./data";
import { useLocations } from "@hooks/useLocations";

export interface ScreenInfo {
  name: string;
  deviceId?: string;
  locationId?: string;
  screenType?: ScreenIdType;
  zone_id?: number;
  group_id?: number;
  id?: string;
  music_infos?: Record<string, any>[];
  last_music_id?: number | null;
  music_playing?: boolean;
  music_status?: 'active' | 'demo' | null
}

interface ScreenInfoFormProps {
  screenName: string;
}

function ScreenInfoPage({
  screen,
  onSubmitted,
  initialScreenInfo,
  screenCreateStatus,
  action,
}: {
  screen?: Screen;
  screenCreateStatus: DataFetchStatus;
  onSubmitted: (screenInfo: ScreenInfo) => void;
  onPairLater?: () => void;
  initialScreenInfo: ScreenInfo | null | undefined;
  action?: React.ReactNode;
}) {
  const { locations } = useLocations()
  const { log } = useGzLog()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScreenInfoFormProps>();

  const [selectedScreenType, setSelectedScreenType] =
    useState<ScreenIdType | null>();

  const [selectedLocation, setSelectedLocation] = useState<
    Location | undefined
  >();

  const { showErrorSnackbar } = useDialog();

  useEffect(() => {
    if (screen || initialScreenInfo) {
      const _location = locations.find((l) => l.id === initialScreenInfo?.locationId || l.id === screen?.location_id)
      if (_location) {
        setSelectedLocation(_location);
      }

      setSelectedScreenType(
        initialScreenInfo?.screenType ?? (screen?.screen_type as ScreenIdType),
      );
    }
  }, [locations, screen, initialScreenInfo]);

  const onSubmit = async (formData: ScreenInfoFormProps) => {
    if (selectedScreenType) {
      onSubmitted({
        name: formData.screenName,
        locationId: selectedLocation?.id,
        screenType: selectedScreenType,
      });
      log({
        name: "ScreenInfoUpdated",
        eventtype: "Action",
        payload: {
          screenId: screen?.id ?? "undefined",
          screenName: formData.screenName,
          locationId: selectedLocation?.id ?? "undefined",
          screenType: selectedScreenType,
        },
      });
    } else {
      showErrorSnackbar("Please select a screen type");
    }
  };

  return (
    <form
      key={"screen_info_form"}
      onSubmit={handleSubmit(onSubmit)}
      className="flex h-full flex-col items-center justify-center gap-4 sm:gap-8 pt-4 sm:pt-8"
    >
      <div className="w-full">
        <CustomInputField
          leadingIcon={<FiMonitor />}
          defaultValue={initialScreenInfo?.name ?? screen?.name ?? ""}
          placeholder="Screen Name"
          {...register("screenName", { required: true })}
        />
        {errors.screenName && (
          <span className="input-field-error pt-1 sm:pt-2">
            Screen name is required
          </span>
        )}
      </div>
      {locations.length > 0 && (
        <div className="w-full">
          <LocationSelectMenu
            label="Location"
            optionRender={(val) => val.name}
            selectedRender={(val) => val.name}
            selected={selectedLocation}
            options={locations}
            onChange={(location) => {
              if (location) setSelectedLocation(location);
            }}
          />
        </div>
      )}

      {!screen && (
        <div className="grid w-full grid-cols-2 sm:grid-cols-3 flex-wrap justify-center gap-1 sm:gap-2">
          {screenTypes
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((item, index) => {
              return (
                <button
                  type="button"
                  disabled={item.id === "apple"}
                  key={`device_type_${index}`}
                  onClick={() => setSelectedScreenType(item.id)}
                  className={`${item.id === selectedScreenType
                    ? "border-accentColor bg-accentColor bg-opacity-10 text-accentColor dark:border-darkAccentColor dark:bg-darkAccentColor dark:bg-opacity-10 dark:text-darkAccentColor"
                    : "border-lightCard bg-lightCard hover:text-accentColor dark:border-darkCard dark:bg-darkCard dark:hover:text-darkAccentColor"
                    } flex flex-col items-center justify-center gap-2 sm:gap-4 rounded-md border-2 px-4 sm:px-8 py-3 sm:py-4 transition-all disabled:cursor-not-allowed`}
                >
                  <div className="text-5xl sm:text-7xl">{item.icon}</div>
                  <div className="text-xs">{item.title}</div>
                </button>
              );
            })}
        </div>
      )}

      <p className="text-center text-xs text-gray-500">
        Don’t have your TV screen ready yet? You can use your{" "}
        <span className="font-medium italic text-accentColor dark:text-darkAccentColor">
          <Link
            href={env.NEXT_PUBLIC_PLAYER_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            browser
          </Link>
        </span>{" "}
        as a screen instead.
      </p>
      <div className="flex gap-4 items-center justify-center">
        {action}
        <Button
          className="w-36 gap-2"
          type="submit"
          disabled={screenCreateStatus === "fetching"}
        >
          {screen ? "Update" : "Next"}
          {screenCreateStatus === "fetching" ? (
            <CircularProgressIndicator />
          ) : (
            <>{screen ? <MdUpdate className="size-4" /> : <MdArrowForward className="size-4" />}</>
          )}
        </Button>
      </div>
    </form>
  );
}

export default ScreenInfoPage;
